import React from 'react';
import './styles.scss';
import strings from 'utils/strings';
import classNames from 'classnames';
import AlertBox from 'components/CreateNotification/alert';
import getAllGroups from 'api/getAllGroups';
import transferTests from 'api/transferTests';
import ConfirmationModal from './confirmationModel';

class TransferTest extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			fromUsers: [],
			toUser: '',
			toGroup: '',
			groupData: [],
			filteredGroups: [],
			destinationType:
				props.selectedUsers?.length === 1 ? 'group' : 'user',
			showGroupDropdown: false,
			groupSearch: '',
			showAlert: false,
			showConfirmModal: false,
			success: false,
			error: false,
			message: '',
		};
		this.dropdownRef = React.createRef();
	}

	componentDidMount() {
		getAllGroups().then((groupData) => {
			const uniqueGroups = Array.from(
				new Map(groupData.map((g) => [g.Id, g])).values()
			);

			this.setState({
				groupData: uniqueGroups,
				filteredGroups: uniqueGroups,
			});
		});

		document.addEventListener('click', this.handleClickOutside);
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClickOutside);
	}

	handleClickOutside = (e) => {
		if (
			this.dropdownRef.current &&
			!this.dropdownRef.current.contains(e.target)
		) {
			this.setState({showGroupDropdown: false});
		}
	};

	toggleFromUser = (user) => {
		const subId = user?.subscription?.id;
		if (!subId) return;

		this.setState((prev) => {
			const alreadySelected = prev.fromUsers.includes(subId);
			const updated = alreadySelected
				? prev.fromUsers.filter((id) => id !== subId)
				: [...prev.fromUsers, subId];

			return {
				fromUsers: updated,
				toUser: updated.includes(prev.toUser) ? '' : prev.toUser,
			};
		});
	};

	handleToUserChange = (e) => this.setState({toUser: e.target.value});

	handleDestinationTypeChange = (e) => {
		this.setState({
			destinationType: e.target.value,
			toUser: '',
			toGroup: '',
			groupSearch: '',
			showGroupDropdown: false,
		});
	};

	handleTransfer = () => {
		const {fromUsers, toUser, toGroup, destinationType} = this.state;
		const targetId = destinationType === 'user' ? toUser : toGroup;

		if (!fromUsers.length || !targetId) {
			this.setState({
				showAlert: true,
				success: false,
				error: true,
				message: strings.transferSelectSourceAndTarget,
			});
			setTimeout(() => this.setState({showAlert: false}), 3000);
			return;
		}

		this.setState({showConfirmModal: true});
	};

	confirmTransfer = () => {
		const {fromUsers, toUser, toGroup, destinationType} = this.state;
		const targetId = destinationType === 'user' ? toUser : toGroup;

		this.setState({showConfirmModal: false});

		transferTests(fromUsers, destinationType, targetId)
			.then((res) => {
				if (!res || res.error) {
					this.setState({
						showAlert: true,
						success: false,
						error: true,
						message:
							res?.message ||
							strings.transferFailedTryAgain,
					});
					setTimeout(() => this.setState({showAlert: false}), 3000);
					return;
				}

				this.setState({
					showAlert: true,
					success: true,
					error: false,
					message: `Successfully transferred ${fromUsers.length} user(s) to the selected ${destinationType}.`,
				});

				setTimeout(() => {
					this.setState({showAlert: false});
					this.props.closeWindow();
				}, 2500);
			})
			.catch((err) => {
				console.error('Transfer failed:', err);
				this.setState({
					showAlert: true,
					success: false,
					error: true,
					message: strings.transferServerError,
				});
				setTimeout(() => this.setState({showAlert: false}), 3000);
			});
	};

	cancelTransfer = () => {
		this.setState({showConfirmModal: false});
	};

	handleGroupSearch = (e) => {
		const searchValue = e.target.value.toLowerCase();
		this.setState((prev) => ({
			groupSearch: searchValue,
			filteredGroups: prev.groupData.filter((group) =>
				group.groupName.toLowerCase().includes(searchValue)
			),
			toGroup: '',
			showGroupDropdown: true,
		}));
	};

	selectGroup = (groupId, groupName) => {
		this.setState({
			toGroup: String(groupId),
			groupSearch: groupName,
			showGroupDropdown: false,
		});
	};

	getDisableReason = () => {
		const {fromUsers, toUser, toGroup, destinationType} = this.state;

		if (!fromUsers.length) {
			return strings.transferMustSelectSourceUser;
		}
		if (destinationType === 'user' && !toUser) {
			return strings.transferSelectDestinationUser;
		}
		if (destinationType === 'group' && !toGroup) {
			return strings.transferSelectDestinationGroup;
		}
		return '';
	};

	render() {
		const allUsers = this.props.selectedUsers;
		const {
			fromUsers,
			toUser,
			groupSearch,
			filteredGroups,
			showGroupDropdown,
			destinationType,
			showAlert,
			showConfirmModal,
			success,
			message,
		} = this.state;

		const disableReason = this.getDisableReason();
		const isDisabled = Boolean(disableReason);

		// Exclude users whose subscription.id is already selected
		const availableToUsers = allUsers.filter(
			(u) => !fromUsers.includes(u?.subscription?.id)
		);

		return (
			<div className="lit-modal" onClick={() => this.props.closeWindow()}>
				<div
					className="lit-modal__island transfer-container"
					onClick={(e) => e.stopPropagation()}
				>
					<h3 className="transfer-title">{strings.transferTest}</h3>

					{/* FROM + TO SECTIONS */}
					<div className="transfer-section">
						{/* FROM USERS */}
						<div className="transfer-box from-box">
							<label className="transfer-label">
								{strings.transferFromUsersLabel}
							</label>
							<p className="transfer-helper">
								{strings.transferClickUsersHelper}
							</p>
							<div className="transfer-list">
								{allUsers.map((user) => {
									const subId = user?.subscription?.id;
									const selected = fromUsers.includes(subId);
									return (
										<div
											key={user.id}
											className={`transfer-user-item ${
												selected ? 'selected' : ''
											}`}
											onClick={() =>
												this.toggleFromUser(user)
											}
										>
											<div className="transfer-user-name">
												{user.firstName} {user.lastName}
											</div>
											<div className="transfer-user-email">
												{user.email}
											</div>
										</div>
									);
								})}
							</div>
							{fromUsers.length > 0 && (
								<p className="transfer-count">
									{fromUsers.length}{' '}
									{strings.transferUsersSelectedCount}
								</p>
							)}
						</div>

						<div className="arrow-indicator">➡️</div>

						{/* DESTINATION */}
						<div className="transfer-box to-box">
							<label className="transfer-label">
								{strings.transferDestinationLabel}
							</label>

							<div className="transfer-toggle">
								<label>
									<input
										type="radio"
										value="user"
										checked={destinationType === 'user'}
										onChange={
											this.handleDestinationTypeChange
										}
									/>
									{strings.transferToUserOption}
								</label>
								<label>
									<input
										type="radio"
										value="group"
										checked={destinationType === 'group'}
										onChange={
											this.handleDestinationTypeChange
										}
									/>
									{strings.transferToGroupOption}
								</label>
							</div>

							{/* DESTINATION SELECT */}
							{destinationType === 'user' ? (
								<>
									<p className="transfer-helper">
										{strings.transferSelectTargetUserHelper}
									</p>
									<select
										value={toUser}
										onChange={this.handleToUserChange}
										className="transfer-select"
									>
										<option value="">
											{strings.transferSelectUserPlaceholder}
										</option>
										{availableToUsers.map((user) => (
											<option
												key={user.id}
												value={user.subscription?.id}
											>
												{user.firstName} {user.lastName}{' '}
												({user.email})
											</option>
										))}
									</select>
								</>
							) : (
								<>
									<p className="transfer-helper">
										{strings.transferSearchSelectGroupHelper}
									</p>
									<div
										className="group-select-wrapper"
										ref={this.dropdownRef}
									>
										<input
											type="text"
											value={groupSearch}
											onChange={this.handleGroupSearch}
											onFocus={() =>
												this.setState({
													showGroupDropdown: true,
												})
											}
											placeholder={strings.transferSearchGroupsPlaceholder}
											className="transfer-select group-search-box"
										/>

										{showGroupDropdown && (
											<div className="group-dropdown">
												{filteredGroups.length > 0 ? (
													filteredGroups.map(
														(group, index) => (
															<div
																key={index}
																className="group-option"
																onClick={() =>
																	this.selectGroup(
																		group.Id,
																		group.groupName
																	)
																}
															>
																{
																	group.groupName
																}
															</div>
														)
													)
												) : (
													<div className="group-option empty">
														{strings.transferNoGroupsFound}
													</div>
												)}
											</div>
										)}
									</div>
								</>
							)}
						</div>
					</div>
					{isDisabled && (
						<p className="transfer-disabled-reason">
							⚠️ {disableReason}
						</p>
					)}
					{/* ACTION BUTTONS */}
					<div className="lit_create_new-group transfer-actions">
						<button
							disabled={isDisabled}
							className={classNames('print-hide', {
								'lit-btn see-more': true,
								'bg-tertiary': true,
							})}
							onClick={this.handleTransfer}
						>
							{strings.transferTestsButton}
						</button>

						<button
							className="lit-btn bg-negative2"
							onClick={() => this.props.closeWindow()}
						>
							{strings.cancel}
						</button>
					</div>
				</div>

				{/* Confirmation Modal */}
				{showConfirmModal && (
					<ConfirmationModal
						onConfirm={this.confirmTransfer}
						onCancel={this.cancelTransfer}
					/>
				)}

				{/* Alert Messages */}
				{showAlert && (
					<AlertBox
						type={success ? 'success' : 'error'}
						message={message}
					/>
				)}
			</div>
		);
	}
}

export default TransferTest;
